import pick from 'lodash/pick';

import {userDetailsStore} from 'common/report/stores/user-details';
import {QueryReportHeaderAttrs, queryReportHeaderAttrsSchema} from 'common/widgets/query-report-header/types';
import {capitalize, pluralize, toTitleCase} from 'common/util/string';
import {promisifyModal} from 'common/components/modal/utils';
import {confirm} from 'common/components/confirm';
import {emitTrackEvent} from 'common/util/dom';
import {SearchDisplayContext, SearchResultType} from 'common/report/util/search/types';
import {ProjectDetails} from 'common/report/stores/project-details';
import {FeatureGate} from 'common/report/constants';
import {Cohort} from 'common/widgets/cohorts-query-builder/models/cohort';
import {GlobalAccessType} from 'common/widgets/create-entity-modal/types';

import {getProjectId} from '../index';
import {
  CustomProperty,
  Dashboard,
  Entity,
  EntityType,
  EntityTypeKey,
  EntityTypeNamePlural,
  EntityTypeNameSingular,
  EntityTypePermission,
  EntityTypePermissions,
  GlobalAccessParamName,
  OverrideModal,
  Report,
  SharableEntityType,
  UserRole,
} from './types';

// TODO evnp - add JSDoc comments to all these utils when there's bandwidth

export function getEntityTypeName(
  entityType: EntityType,
  {
    plural = false,
    numEntities = null, // may be used instead of `plural` flag when convenient
    capitalized = false, // first word capitalized
    titleCased = false, // every word capitalized
    article = null, // "a", "the", "this", "your", etc.
    articlePlural = null, // "the", "these", "your", etc.
  }: {
    plural?: boolean;
    numEntities?: number;
    capitalized?: boolean;
    titleCased?: boolean;
    article?: string;
    articlePlural?: string;
  } = {},
) {
  const pluralizeArg = numEntities !== null ? numEntities : plural;
  const nameSingular = EntityTypeNameSingular[EntityTypeKey[entityType]];
  const namePlural = EntityTypeNamePlural[EntityTypeKey[entityType]];

  let name = pluralize(nameSingular, pluralizeArg, namePlural);

  if (article || articlePlural) {
    // TODO when entities appear with non-standard articles (e.g. "an" vs "a") add remappings here
    article = article || `a`;
    articlePlural = articlePlural || article;
    name = `${pluralize(article, pluralizeArg, articlePlural)} ${name}`;
  }

  // first word capitalized
  if (capitalized) {
    name = capitalize(name);
  }

  // every word capitalized
  if (titleCased) {
    name = toTitleCase(name);
  }

  return name;
}

export function isSharingEnabledForProject(project: ProjectDetails) {
  return project?.getFeatureGate(FeatureGate.Sharing);
}

export function getEntityTypePermission(entityType: EntityType): EntityTypePermission {
  return {
    [EntityType.Report]: EntityTypePermission.Report,
    [EntityType.Dashboard]: EntityTypePermission.Dashboard,
    [EntityType.Cohort]: EntityTypePermission.Cohort,
    [EntityType.CustomEvent]: EntityTypePermission.CustomEvent,
    [EntityType.CustomProperty]: EntityTypePermission.CustomProperty,
    [EntityType.Workspace]: EntityTypePermission.Workspace,
  }[entityType];
}

const ENTITY_TYPE_TO_SHARABLE_ENTITY_TYPE = {
  [EntityType.Report]: SharableEntityType.Report,
  [EntityType.Dashboard]: SharableEntityType.Dashboard,
  [EntityType.Cohort]: SharableEntityType.Cohort,
  [EntityType.CustomProperty]: SharableEntityType.CustomProperty,
  [EntityType.CustomEvent]: SharableEntityType.CustomEvent,
};

const SEARCH_ENTITY_TYPE_TO_SHARABLE_ENTITY_TYPE: {[k in SearchResultType]: SharableEntityType} = {
  [SearchResultType.Campaign]: null,
  [SearchResultType.Cohort]: SharableEntityType.Cohort,
  [SearchResultType.Dashboard]: SharableEntityType.Dashboard,
  [SearchResultType.Event]: SharableEntityType.CustomEvent,
  [SearchResultType.Help]: null,
  [SearchResultType.Message]: null,
  [SearchResultType.Report]: SharableEntityType.Report,
};

const SEARCH_ENTITY_TYPE_TO_ENTITY_TYPE: {[k in SearchResultType]: EntityType} = {
  [SearchResultType.Campaign]: null,
  [SearchResultType.Cohort]: EntityType.Cohort,
  [SearchResultType.Dashboard]: EntityType.Dashboard,
  [SearchResultType.Event]: EntityType.CustomEvent,
  [SearchResultType.Help]: null,
  [SearchResultType.Message]: null,
  [SearchResultType.Report]: EntityType.Report,
};

export function getSharableEntityType(entityType: EntityType | SearchResultType): SharableEntityType {
  const sharableEntityType =
    ENTITY_TYPE_TO_SHARABLE_ENTITY_TYPE[entityType] ?? SEARCH_ENTITY_TYPE_TO_SHARABLE_ENTITY_TYPE[entityType];

  if (!sharableEntityType) {
    throw new Error(`Entity type ${entityType} is not shareable.`);
  }

  return sharableEntityType;
}

export function isSharableEntityType(entityType: EntityType): boolean {
  return Object.keys(ENTITY_TYPE_TO_SHARABLE_ENTITY_TYPE).includes(entityType);
}

export function getEntityType(sharableOrSearchType: SharableEntityType | SearchResultType): EntityType {
  const entityType =
    ({
      [SharableEntityType.Report]: EntityType.Report,
      [SharableEntityType.Cohort]: EntityType.Cohort,
      [SharableEntityType.CustomEvent]: EntityType.CustomEvent,
      [SharableEntityType.CustomProperty]: EntityType.CustomProperty,
      [SharableEntityType.Dashboard]: EntityType.Dashboard,
    } as Record<SharableEntityType, EntityType>)[sharableOrSearchType] ??
    SEARCH_ENTITY_TYPE_TO_ENTITY_TYPE[sharableOrSearchType];

  if (!entityType) {
    throw new Error(`Entity type ${sharableOrSearchType} does not map to an entity.`);
  }

  return entityType;
}

export function getEntityTypePermissions(projectId: number, entityType: EntityType): EntityTypePermissions {
  const project = userDetailsStore.value.projects?.[projectId];
  // project/org permissions may be sets; convert to array for compatibility
  const permissions = Array.from(project?.permissions || []);
  const role = (project?.role?.name as UserRole) || UserRole.Consumer; // default to Consumer role; minimal permissions

  // staff users can do anything in projects they are not a member of
  if (!project && userDetailsStore.value.isStaff) {
    return {
      role: UserRole.Admin,
      can_update_basic: true,
      can_share: true,
    };
  }

  return {
    role,
    can_update_basic: permissions.includes(getEntityTypePermission(entityType)),
    can_share: role !== UserRole.Consumer,
  };
}

// TODO opslead
// eslint-disable-next-line sonarjs/cognitive-complexity
export function buildCreateEntityModalAttrs({
  projectId,
  entityType,
  entity,
  modalOpen,
  isEditing,
  isDuplicating,
  editWarning,
  modalTitleOverride,
  showShareManagement,
}: {
  projectId: number;
  entityType: EntityType;
  entity: Entity | null;
  modalOpen: boolean;
  isEditing: boolean;
  isDuplicating: boolean;
  editWarning?: string;
  modalTitleOverride?: string;
  showShareManagement?: boolean;
}) {
  const entityTypePermissions = getEntityTypePermissions(projectId, entityType);
  const isConsumer = entityTypePermissions.role === UserRole.Consumer;
  const visibilityControlsMessage =
    isConsumer &&
    `Due to your project role (Consumer), your ${getEntityTypeName(entityType, {
      plural: true,
    })} are always hidden and not editable by others`;

  if (entity && isDuplicating && !entity.name.endsWith(`(copy)`)) {
    // append "(copy)" onto name
    entity = {
      ...entity,
      name: `${entity.name} (copy)`,
    };
  }

  const entityTypeName = getEntityTypeName(entityType, {titleCased: true});
  let modalTitle = `Create New ${entityTypeName}`;
  if (isDuplicating) {
    modalTitle = `Create New ${entityTypeName}`;
  } else if (isEditing) {
    modalTitle = `Edit ${entityTypeName}`;
  }

  if (modalTitleOverride) {
    modalTitle = modalTitleOverride;
  }
  return {
    open: modalOpen,
    'project-id': projectId,
    'modal-title': modalTitle,
    'entity-type': entityType,
    'is-editing': isEditing,
    'initial-name': (entity && entity.name) || ``,
    'initial-description': (entity && entity.description) || ``,
    warning: isEditing && editWarning ? editWarning : ``,
    'show-share-management': !isConsumer && showShareManagement,
    // dogfooding globalAccess in MuzikFinder project
    'show-global-access-menu': projectId === 2074348,

    // sharing-disabled (visibility controls in create-entity-modal)
    'visibility-controls-message': visibilityControlsMessage || ``,
  };
}

// TODO opslead
// eslint-disable-next-line sonarjs/cognitive-complexity
export function buildQueryReportHeaderAttrs({
  projectId,
  entityType = EntityType.Report,
  entity = null,
  invalidEntity = false,
  invalidMessage = null,
  displayContext = null,
}: {
  projectId: number;
  entityType?: EntityType;
  entity: Entity | null;
  invalidEntity?: boolean;
  invalidMessage?: string;
  displayContext: SearchDisplayContext;
}) {
  const entityTypePermissions = getEntityTypePermissions(projectId, entityType);
  const isUnsaved = !(entity && entity.id);

  const canWrite = entityTypePermissions.can_update_basic;
  const canSave = !invalidEntity && (isUnsaved || entity.can_update_basic);
  const canShare = !isUnsaved && entity.can_share;

  return {
    'project-id': projectId,
    'selected-bookmark-id': (entity && entity.id) || null,
    'selected-bookmark-user-id': (entity && entity.userId) || null,
    'selected-bookmark-user-name': (entity && entity.userName) || null,
    'selected-bookmark-user-email': (entity && entity.userEmail) || null,
    'selected-bookmark-name': (entity && entity.name) || ``,
    'selected-bookmark-description': (entity && entity.description) || ``,
    'invalid-entity': invalidEntity,
    'invalid-message': invalidMessage,
    'display-context': displayContext,

    // permissions
    'can-write': canWrite,
    'can-save': canSave,
    'can-share': canShare,
    'is-superadmin': entity?.is_superadmin || entityTypePermissions.role === UserRole.Owner,
    'allow-staff-override': entity?.allow_staff_override || allowStaffOverride(projectId),
  };
}

export function normalizeReportEntity(report: Report | null): Entity | null {
  return !report
    ? null
    : {
        id: report.id,
        userId: report.userId,
        userName: report.userName,
        userEmail: report.userEmail,
        name: report.title || report.name,
        description: report.description,

        // permissions
        can_view: !!report.can_view,
        can_update_basic: !!report.can_update_basic,
        can_share: !!report.can_share,
        is_superadmin: !!report.is_superadmin,
        allow_staff_override: !!report.allow_staff_override,
      };
}

export function normalizeDashboardEntity(dashboard: Dashboard | null): Entity | null {
  return !dashboard
    ? null
    : {
        id: dashboard.id,
        userId: dashboard.creator_id,
        userName: dashboard.creator_name,
        userEmail: dashboard.creator_email,
        name: dashboard.title,
        description: dashboard.description,

        // permissions
        can_view: !!dashboard.can_view,
        can_update_basic: !!dashboard.can_update_basic,
        can_share: !!dashboard.can_share,
        is_superadmin: !!dashboard.is_superadmin,
        allow_staff_override: !!dashboard.allow_staff_override,
      };
}

export function normalizeCohortEntity(cohort: Cohort | null): Entity | null {
  return !cohort
    ? null
    : {
        id: cohort.id,
        userId: cohort.createdBy?.id || cohort[`created_by`]?.id,
        userName: cohort.createdBy?.name || cohort[`created_by`]?.name,
        userEmail: cohort.createdBy?.email || cohort[`created_by`]?.email,
        name: cohort.name,
        description: cohort.description,

        // permissions
        can_view: !!cohort.can_view,
        can_update_basic: !!cohort.can_update_basic,
        can_share: !!cohort.can_share,
        is_superadmin: !!cohort.is_superadmin,
        allow_staff_override: !!cohort.allow_staff_override,
      };
}

export function normalizeCustomPropertyEntity(customProperty: CustomProperty | null): Entity | null {
  return !customProperty
    ? null
    : {
        id: null, // only ever used for creating
        userId: customProperty.userId || customProperty.user, // TODO @evnp - eliminate "user" attr for user ID, super confusing
        userName: customProperty.userName,
        userEmail: customProperty.userEmail,
        name: customProperty.name,
        description: customProperty.description,

        // permissions
        can_view: !!customProperty.can_view,
        can_update_basic: !!customProperty.canUpdateBasic,
        can_share: !!customProperty.can_share,
        is_superadmin: !!customProperty.is_superadmin,
        allow_staff_override: !!customProperty.allow_staff_override,
      };
}

export function pickQueryReportHeaderAttrs(attrsObject: {[key: string]: any}): QueryReportHeaderAttrs {
  return pick(attrsObject, Object.keys(queryReportHeaderAttrsSchema)) as QueryReportHeaderAttrs;
}

/**
 * TODO: @junhouse move dashboard to use the same splitEntities func
 * Split entities into
 * allEntities
 * visibleEntities = visible & creator != current user
 * yourEntities = creator == current user
 * @param entities
 * @param currentUserId
 */
export function splitEntities(
  entities: any[],
  currentUserId: number,
): {
  allEntities: any[];
  yourEntities: any[];
  visibleEntities: any[];
} {
  const allEntities = entities || [];
  const getEntityCreatorId = (entity: any) =>
    normalizeReportEntity(entity).userId || normalizeCohortEntity(entity).userId;
  const allVisibleEntities = allEntities.filter((entity) => entity.can_view);
  const yourEntities = allEntities.filter((entity) => getEntityCreatorId(entity) === currentUserId);
  const visibleEntities = allVisibleEntities.filter((entity) => getEntityCreatorId(entity) !== currentUserId);
  return {
    allEntities,
    yourEntities,
    visibleEntities,
  };
}

interface EntitySection {
  label: string;
  items: any[];
  loading: boolean;
}
/**
 * Creates an array for the query-report-header depending on share FF and owner
 * @param yourEntities
 * @param visibleEntities
 * @param allEntities
 * @param userIsOwner
 * @param canShare
 */
export function createEntitiesSectionsArray({
  yourEntities = {
    label: ``,
    items: [],
    loading: false,
  },
  visibleEntities = {
    label: ``,
    items: [],
    loading: false,
  },
  allEntities = {
    label: ``,
    items: [],
    loading: false,
  },
  userIsOwner = false,
  canShare = false,
}: {
  yourEntities: EntitySection;
  visibleEntities: EntitySection;
  allEntities: EntitySection;
  userIsOwner: boolean;
  canShare: boolean;
}): any[] {
  if (!canShare) {
    return [yourEntities, allEntities];
  } else if (userIsOwner) {
    return [yourEntities, visibleEntities, allEntities];
  } else {
    return [yourEntities, visibleEntities];
  }
}

interface OverrideOptions {
  isBulkAction?: boolean;
  selectedEntities?: Dashboard[];
  selectedSuperadminOverrideEntities?: Dashboard[];
}
export async function promptSuperadminOverrideSave(
  entity: EntityType,
  options: OverrideOptions = {},
): Promise<OverrideModal> {
  const isStaff = allowStaffOverride(getProjectId());
  let outcome = OverrideModal.Closed;

  const isBulkAction = options.isBulkAction;
  let entityTypeName = getEntityTypeName(entity);
  let confirmLabel = `Yes, Add`;
  let description = `The creator of this ${entityTypeName} has not given you edit access.\n\nAs a ${
    isStaff ? `staff user` : `project Owner`
  } you can override this setting. Would you like to continue adding this report to the selected dashboard?`;

  if (isBulkAction) {
    const entities = options.selectedEntities ? options.selectedEntities : [];
    const superadminEntities = options.selectedSuperadminOverrideEntities
      ? options.selectedSuperadminOverrideEntities
      : [];
    const entitiesCount = entities.length;
    const superadminEntitiesCount = superadminEntities.length;

    if (superadminEntitiesCount) {
      entityTypeName = getEntityTypeName(entity, {plural: superadminEntitiesCount > 1});
      const hasNonOverrideEntities = superadminEntitiesCount !== entitiesCount;
      const additionalText = hasNonOverrideEntities ? `out of ${entitiesCount}` : ``;
      if (hasNonOverrideEntities) {
        confirmLabel = `Yes, Add to All`;
      }
      const superadminEntitiesNames = superadminEntities.map((dashboard) => `• ${dashboard.title}`).join(`\n`);
      description =
        `You have not been given edit access to the following ${superadminEntitiesCount} ${additionalText} ${entityTypeName}:\n\n` +
        superadminEntitiesNames +
        `\n\nAs a ${
          isStaff ? `staff user` : `project Owner`
        } you can override this setting. Would you like to continue adding this report to the selected dashboards?`;
    }
  }
  const {detail} = await promisifyModal({
    tagName: `mp-confirm`,
    attrs: {
      title: `You Don't Have Access`,
      description,
      'description-allow-newline': true,
      closeable: true,
      'cancel-button-label': isBulkAction ? `Cancel` : `No, Make a Copy`,
      'confirm-button-label': isBulkAction ? confirmLabel : `Yes, Save`,
      elref: `superAdminOverrideSave`,
    },
    closeEvents: [`submit`],
  });
  if (detail.action === `cancel`) {
    outcome = OverrideModal.Copy;
  } else if (detail.action === `confirm`) {
    outcome = OverrideModal.Save;
  }

  emitTrackEvent(window, {
    eventName: `[Override] Save`,
    props: {
      'Entity Type': entity,
      Outcome: outcome,
      'Staff Override': isStaff,
    },
  });

  return outcome;
}

export async function promptSuperadminOverrideShare(entity: EntityType): Promise<boolean> {
  const isStaff = allowStaffOverride(getProjectId());
  const outcome = await confirm({
    title: `You Don't Have Access`,
    description: `The creator of this ${getEntityTypeName(entity)} has not given you edit access to share.\n\nAs a ${
      isStaff ? `staff user` : `project Owner`
    } you can override this setting. Would you like to continue?`,
    'description-allow-newline': true,
    closeable: true,
    'confirm-button-label': `Yes, Share`,
  });

  emitTrackEvent(window, {
    eventName: `[Override] Share`,
    props: {
      'Entity Type': entity,
      Outcome: outcome,
      'Staff Override': isStaff,
    },
  });

  return outcome;
}

export function getUserAccess(entity, {ownerId, userId}: {ownerId: number; userId: number}) {
  return ownerId === userId
    ? `Owner`
    : entity.can_update_basic || entity.canUpdateBasic
    ? `Editor`
    : entity.can_view || entity.canView
    ? `Viewer`
    : `None`;
}

interface ShareCapability {
  enableShare: boolean;
  requireSuperadminOverride: boolean;
  disabledShareReason: string;
}

/** Is the user allowed to override standard grant permissions */
export function isAllowedSuperadminOverride(entity): boolean {
  return entity
    ? Boolean(
        entity.allowStaffOverride ||
          entity.allow_staff_override ||
          ((entity.isSuperadmin || entity.is_superadmin) && !(entity.canUpdateBasic || entity.can_update_basic)),
      )
    : false;
}

// Determine if a user has share capability for a given entity
export function getShareCapability(
  entity,
  entityType: EntityType = EntityType.Report,
  opts: {isSharingEnabled: boolean} = {isSharingEnabled: true},
): ShareCapability {
  // For projects that do not have the sharing feature gate, we enable share button
  // but show the upsell banner in share modal
  if (!opts.isSharingEnabled) {
    return {
      enableShare: true,
      requireSuperadminOverride: false,
      disabledShareReason: ``,
    };
  }

  if (!entity) {
    return {
      enableShare: false,
      requireSuperadminOverride: false,
      disabledShareReason: ``,
    };
  }

  // User is not given an editor access to share, but has superadmin or staff privilege
  if (isAllowedSuperadminOverride(entity)) {
    return {
      enableShare: true,
      requireSuperadminOverride: true,
      disabledShareReason: ``,
    };
  }

  if (!(entity.canShare || entity.can_share) || !(entity.canUpdateBasic || entity.can_update_basic)) {
    const canShare = entity.canShare || entity.can_share;
    const canUpdateBasic = entity.canUpdateBasic || entity.can_update_basic;
    const entityTypeNamePlural = getEntityTypeName(entityType, {plural: true});
    let disabledShareReason = ``;
    // Consumers can update their reports but cannot share
    if (!canShare && canUpdateBasic) {
      disabledShareReason = `Due to your project role(Consumer), your ${entityTypeNamePlural} are always hidden and cannot be shared with others`;
    } else {
      disabledShareReason = `You have view-only access and cannot share`;
    }
    return {
      enableShare: false,
      requireSuperadminOverride: false,
      disabledShareReason,
    };
  }

  return {
    enableShare: true,
    requireSuperadminOverride: false,
    disabledShareReason: ``,
  };
}

/** is the user staff without membership to the project */
export function allowStaffOverride(projectId: number | string): boolean {
  const {isStaff, projects} = userDetailsStore.value;

  return isStaff && !projects[projectId];
}

export function getGlobalAccessTypeParam(globalAccessType: GlobalAccessType): string {
  switch (globalAccessType) {
    case GlobalAccessType.OFF:
      return `off`;
    case GlobalAccessType.VIEWER:
      return `viewer`;
    case GlobalAccessType.EDITOR:
      return `editor`;
    default:
      return `off`;
  }
}

/** Add global_access_type param to params for CREATE APIs.*/
export function maybeAddGlobalAccessTypeParam(params, saveAsNew, globalAccessType) {
  return {
    ...params,
    ...(saveAsNew ? {[GlobalAccessParamName]: getGlobalAccessTypeParam(globalAccessType || GlobalAccessType.OFF)} : {}),
  };
}
